import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import Layout from '../components/Layout';
import { 
  CreditCard, Search, X, Clock, AlertCircle, 
  Building2, CheckCircle, Loader2, FileDown, Truck, Wheat, Plus
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import ReciboPagoPDF from '../utils/ReciboPagoPDF';

const API = process.env.REACT_APP_BACKEND_URL;

// Estilos para estados de pago
const ESTADO_STYLES = {
  'Pendiente': 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200',
  'Emitido': 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200',
  'Pagado': 'bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200'
};

const ESTADO_ICONS = {
  'Pendiente': Clock,
  'Emitido': AlertCircle,
  'Pagado': CheckCircle
};

// Orden de estados para el ciclo
const ESTADO_CYCLE = ['Pendiente', 'Emitido', 'Pagado'];

const Pagos = () => {
  const { user, token } = useAuth();
  
  // Tab activa: 'flete' o 'granos'
  const [activeTab, setActiveTab] = useState('flete');
  
  // Datos de Flete
  const [liquidaciones, setLiquidaciones] = useState([]);
  const [resumenFlete, setResumenFlete] = useState(null);
  
  // Datos de Granos
  const [pagosGranos, setPagosGranos] = useState([]);
  const [pagosGranosPendientes, setPagosGranosPendientes] = useState([]);
  const [resumenGranos, setResumenGranos] = useState(null);
  
  const [loading, setLoading] = useState(true);
  
  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('all');
  const [filtroTipo, setFiltroTipo] = useState('all');
  
  // Estado para edición inline
  const [editingRow, setEditingRow] = useState(null);
  const [editData, setEditData] = useState({});
  const [savingId, setSavingId] = useState(null);

  // Fetch data según tab activa
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      if (activeTab === 'flete') {
        const [liqRes, resumenRes] = await Promise.all([
          axios.get(`${API}/api/liquidaciones`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/api/pagos/resumen`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setLiquidaciones(liqRes.data);
        setResumenFlete(resumenRes.data);
      } else {
        const [pagosRes, pendientesRes, resumenRes] = await Promise.all([
          axios.get(`${API}/api/pagos-granos`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/api/pagos-granos/pendientes`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/api/pagos-granos/resumen`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        setPagosGranos(pagosRes.data);
        setPagosGranosPendientes(pendientesRes.data);
        setResumenGranos(resumenRes.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [token, activeTab]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filtrar liquidaciones
  const filteredLiquidaciones = useMemo(() => {
    return liquidaciones.filter(l => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const matchSearch = 
          l.dispatch_number?.toLowerCase().includes(term) ||
          l.company_name?.toLowerCase().includes(term) ||
          l.factura_transporte?.toLowerCase().includes(term) ||
          l.numero_cheque?.toLowerCase().includes(term);
        if (!matchSearch) return false;
      }
      if (filtroEstado !== 'all') {
        const estado = l.pago_estado || 'Pendiente';
        if (estado !== filtroEstado) return false;
      }
      if (filtroTipo !== 'all' && l.pago_tipo !== filtroTipo) return false;
      return true;
    });
  }, [liquidaciones, searchTerm, filtroEstado, filtroTipo]);

  // Ciclar estado al hacer clic
  const cycleEstado = async (liq) => {
    if (user.role !== 'MASTER') return;
    
    const currentEstado = liq.pago_estado || 'Pendiente';
    const currentIndex = ESTADO_CYCLE.indexOf(currentEstado);
    const nextEstado = ESTADO_CYCLE[(currentIndex + 1) % ESTADO_CYCLE.length];
    
    try {
      setSavingId(liq.id);
      await axios.put(
        `${API}/api/liquidaciones/${liq.id}/pago`,
        { 
          pago_tipo: liq.pago_tipo || 'Transferencia',
          pago_estado: nextEstado,
          numero_cheque: liq.numero_cheque || '',
          pago_fecha: liq.pago_fecha || '',
          factura_transporte: liq.factura_transporte || ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Actualizar estado local
      setLiquidaciones(prev => prev.map(l => 
        l.id === liq.id ? { ...l, pago_estado: nextEstado } : l
      ));
      
      // Actualizar resumen
      fetchData();
    } catch (error) {
      toast.error('Error al cambiar estado');
    } finally {
      setSavingId(null);
    }
  };

  // Iniciar edición inline
  const startEdit = (liq) => {
    setEditingRow(liq.id);
    setEditData({
      pago_tipo: liq.pago_tipo || '',
      numero_cheque: liq.numero_cheque || '',
      pago_fecha: liq.pago_fecha || '',
      factura_transporte: liq.factura_transporte || ''
    });
  };

  // Cancelar edición
  const cancelEdit = () => {
    setEditingRow(null);
    setEditData({});
  };

  // Guardar edición inline
  const saveEdit = async (liq) => {
    try {
      setSavingId(liq.id);
      await axios.put(
        `${API}/api/liquidaciones/${liq.id}/pago`,
        { 
          pago_tipo: editData.pago_tipo || 'Transferencia',
          pago_estado: liq.pago_estado || 'Pendiente',
          numero_cheque: editData.numero_cheque || '',
          pago_fecha: editData.pago_fecha || '',
          factura_transporte: editData.factura_transporte || ''
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Actualizar estado local
      setLiquidaciones(prev => prev.map(l => 
        l.id === liq.id ? { ...l, ...editData } : l
      ));
      
      toast.success('Datos guardados');
      setEditingRow(null);
      setEditData({});
    } catch (error) {
      toast.error('Error al guardar');
    } finally {
      setSavingId(null);
    }
  };

  // Guardar campo individual al perder foco
  const saveField = async (liq, field, value) => {
    if (user.role !== 'MASTER') return;
    
    const currentValue = liq[field] || '';
    if (value === currentValue) return;
    
    try {
      await axios.put(
        `${API}/api/liquidaciones/${liq.id}/pago`,
        { 
          pago_tipo: field === 'pago_tipo' ? value : (liq.pago_tipo || 'Transferencia'),
          pago_estado: liq.pago_estado || 'Pendiente',
          numero_cheque: field === 'numero_cheque' ? value : (liq.numero_cheque || ''),
          pago_fecha: field === 'pago_fecha' ? value : (liq.pago_fecha || ''),
          factura_transporte: field === 'factura_transporte' ? value : (liq.factura_transporte || '')
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Actualizar estado local
      setLiquidaciones(prev => prev.map(l => 
        l.id === liq.id ? { ...l, [field]: value } : l
      ));
    } catch (error) {
      toast.error('Error al guardar');
      fetchData(); // Recargar datos en caso de error
    }
  };

  // Formato de moneda
  const formatCurrency = (value, currency = 'USD') => {
    if (!value && value !== 0) return '-';
    return `$${value.toLocaleString('es-CL', { minimumFractionDigits: 2 })} ${currency}`;
  };

  // Formato de fecha
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  };

  const isMaster = user.role === 'MASTER';

  // Funciones para pagos de granos
  const registrarPagoGranos = async (pago) => {
    try {
      setSavingId(pago.key);
      await axios.post(`${API}/api/pagos-granos`, pago, { 
        headers: { Authorization: `Bearer ${token}` } 
      });
      toast.success('Pago registrado exitosamente');
      fetchData();
    } catch (error) {
      toast.error('Error al registrar pago');
    } finally {
      setSavingId(null);
    }
  };

  const cycleEstadoGranos = async (pago) => {
    if (!isMaster) return;
    
    // Solo alternar entre Pendiente y Pagado
    const currentEstado = pago.pago_estado || 'Pendiente';
    const nextEstado = currentEstado === 'Pendiente' ? 'Pagado' : 'Pendiente';
    
    try {
      await axios.put(`${API}/api/pagos-granos/${pago.id}`, 
        { pago_estado: nextEstado },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPagosGranos(prev => prev.map(p => 
        p.id === pago.id ? { ...p, pago_estado: nextEstado } : p
      ));
      toast.success(`Estado: ${nextEstado}`);
    } catch (error) {
      toast.error('Error al actualizar estado');
    }
  };

  const updatePagoGranosField = async (pago, field, value) => {
    if (!isMaster) return;
    try {
      await axios.put(`${API}/api/pagos-granos/${pago.id}`, 
        { [field]: value },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPagosGranos(prev => prev.map(p => 
        p.id === pago.id ? { ...p, [field]: value } : p
      ));
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  // Filtrar pagos de granos
  const filteredPagosGranos = useMemo(() => {
    return pagosGranos.filter(p => {
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        const match = p.proveedor_nombre?.toLowerCase().includes(term) ||
                      p.tipo_grano?.toLowerCase().includes(term) ||
                      p.numero_transferencia?.toLowerCase().includes(term);
        if (!match) return false;
      }
      if (filtroEstado !== 'all' && p.pago_estado !== filtroEstado) return false;
      return true;
    });
  }, [pagosGranos, searchTerm, filtroEstado]);

  return (
    <Layout>
      <div className="space-y-4" data-testid="pagos-page">
        {/* Header con tabs */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <CreditCard className="h-6 w-6 text-emerald-600" />
            <div>
              <h1 className="text-xl font-bold text-slate-800">Pagos</h1>
              <p className="text-xs text-slate-500">Control de pagos de flete y proveedores de granos</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setActiveTab('flete')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'flete' 
                ? 'border-emerald-500 text-emerald-700' 
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-flete"
          >
            <Truck size={16} />
            Flete (Transportistas)
          </button>
          <button
            onClick={() => setActiveTab('granos')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'granos' 
                ? 'border-amber-500 text-amber-700' 
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-granos"
          >
            <Wheat size={16} />
            Granos (Proveedores)
          </button>
        </div>

        {/* Contenido según tab activa */}
        {activeTab === 'flete' ? (
          <>
            {/* Resumen de estados FLETE */}
            {resumenFlete && (
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-amber-600" />
                      <span className="font-semibold text-amber-800">Pendientes</span>
                    </div>
                    <span className="text-2xl font-bold text-amber-700">{resumenFlete.Pendiente?.count || 0}</span>
                  </div>
                  <p className="text-sm text-amber-600 mt-2">
                    Total: {formatCurrency(resumenFlete.Pendiente?.total_a_pagar || 0)}
                  </p>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-blue-600" />
                      <span className="font-semibold text-blue-800">Emitidos</span>
                    </div>
                    <span className="text-2xl font-bold text-blue-700">{resumenFlete.Emitido?.count || 0}</span>
                  </div>
                  <p className="text-sm text-blue-600 mt-2">
                    Total: {formatCurrency(resumenFlete.Emitido?.total_a_pagar || 0)}
                  </p>
                </div>
                
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-emerald-600" />
                      <span className="font-semibold text-emerald-800">Pagados</span>
                    </div>
                    <span className="text-2xl font-bold text-emerald-700">{resumenFlete.Pagado?.count || 0}</span>
                  </div>
                  <p className="text-sm text-emerald-600 mt-2">
                    Total: {formatCurrency(resumenFlete.Pagado?.total_a_pagar || 0)}
                  </p>
                </div>
              </div>
            )}

        {/* Filtros */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
          <div className="flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 max-w-xs">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Buscar despacho, empresa, factura..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 h-8 text-xs"
                data-testid="search-pagos"
              />
            </div>
            
            <Select value={filtroEstado} onValueChange={setFiltroEstado}>
              <SelectTrigger className="w-36 h-8 text-xs">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                <SelectItem value="Pendiente">Pendiente</SelectItem>
                <SelectItem value="Emitido">Emitido</SelectItem>
                <SelectItem value="Pagado">Pagado</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={filtroTipo} onValueChange={setFiltroTipo}>
              <SelectTrigger className="w-36 h-8 text-xs">
                <SelectValue placeholder="Tipo pago" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                <SelectItem value="Cheque">Cheque</SelectItem>
                <SelectItem value="Transferencia">Transferencia</SelectItem>
                <SelectItem value="Efectivo">Efectivo</SelectItem>
              </SelectContent>
            </Select>
            
            {(searchTerm || filtroEstado !== 'all' || filtroTipo !== 'all') && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => { setSearchTerm(''); setFiltroEstado('all'); setFiltroTipo('all'); }}
                className="h-8 text-xs text-slate-500"
              >
                <X size={14} className="mr-1" /> Limpiar
              </Button>
            )}
            
            <span className="text-xs text-slate-500 ml-auto">
              {filteredLiquidaciones.length} de {liquidaciones.length}
            </span>
          </div>
        </div>

        {/* Instrucción para MASTER */}
        {isMaster && (
          <p className="text-[11px] text-slate-500 text-center">
            Haz clic en el <span className="font-semibold">estado</span> para cambiarlo. Edita los campos directamente en la tabla.
          </p>
        )}

        {/* Tabla */}
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-500 flex items-center justify-center gap-2">
              <Loader2 className="h-5 w-5 animate-spin" />
              Cargando...
            </div>
          ) : filteredLiquidaciones.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              {searchTerm || filtroEstado !== 'all' || filtroTipo !== 'all' 
                ? 'No se encontraron liquidaciones con los filtros aplicados' 
                : 'No hay liquidaciones registradas'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-2.5 text-left font-semibold text-slate-600">ESTADO</th>
                    <th className="px-3 py-2.5 text-left font-semibold text-slate-600">DESPACHO</th>
                    <th className="px-3 py-2.5 text-left font-semibold text-slate-600">TRANSPORTISTA</th>
                    <th className="px-3 py-2.5 text-left font-semibold text-slate-600">TIPO PAGO</th>
                    <th className="px-3 py-2.5 text-left font-semibold text-slate-600">N° CHEQUE</th>
                    <th className="px-3 py-2.5 text-left font-semibold text-slate-600">FECHA PAGO</th>
                    <th className="px-3 py-2.5 text-left font-semibold text-slate-600">FACT. TRANSP.</th>
                    <th className="px-3 py-2.5 text-right font-semibold text-slate-600">MONTO USD</th>
                    <th className="px-3 py-2.5 text-center font-semibold text-slate-600">RECIBO</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredLiquidaciones.map((liq) => {
                    const estado = liq.pago_estado || 'Pendiente';
                    const EstadoIcon = ESTADO_ICONS[estado];
                    const isEditing = editingRow === liq.id;
                    const isSaving = savingId === liq.id;
                    
                    return (
                      <tr 
                        key={liq.id} 
                        className="hover:bg-slate-50 transition-colors"
                        data-testid={`pago-row-${liq.id}`}
                      >
                        {/* Estado - Clickeable */}
                        <td className="px-3 py-2">
                          <button
                            onClick={() => cycleEstado(liq)}
                            disabled={!isMaster || isSaving}
                            className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-[10px] font-semibold border transition-all ${ESTADO_STYLES[estado]} ${isMaster ? 'cursor-pointer active:scale-95' : 'cursor-default'}`}
                            title={isMaster ? 'Clic para cambiar estado' : ''}
                            data-testid={`estado-${liq.id}`}
                          >
                            {isSaving ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <EstadoIcon size={12} />
                            )}
                            {estado}
                          </button>
                        </td>
                        
                        {/* Despacho - Solo lectura */}
                        <td className="px-3 py-2 font-mono font-semibold text-slate-800">
                          {liq.dispatch_number}
                        </td>
                        
                        {/* Transportista - Solo lectura */}
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1.5">
                            <Building2 size={12} className="text-slate-400" />
                            <span className="text-slate-700">{liq.company_name}</span>
                          </div>
                        </td>
                        
                        {/* Tipo Pago - Editable inline */}
                        <td className="px-3 py-1">
                          {isMaster ? (
                            <Select 
                              value={liq.pago_tipo || ''} 
                              onValueChange={(v) => saveField(liq, 'pago_tipo', v)}
                            >
                              <SelectTrigger className="h-7 text-xs w-28 border-transparent hover:border-slate-300 bg-transparent">
                                <SelectValue placeholder="-" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Cheque">Cheque</SelectItem>
                                <SelectItem value="Transferencia">Transferencia</SelectItem>
                                <SelectItem value="Efectivo">Efectivo</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-slate-600">{liq.pago_tipo || '-'}</span>
                          )}
                        </td>
                        
                        {/* N° Cheque - Editable inline */}
                        <td className="px-3 py-1">
                          {isMaster ? (
                            <Input
                              defaultValue={liq.numero_cheque || ''}
                              onBlur={(e) => saveField(liq, 'numero_cheque', e.target.value.toUpperCase())}
                              placeholder="-"
                              className="h-7 text-xs font-mono w-24 border-transparent hover:border-slate-300 bg-transparent px-1"
                            />
                          ) : (
                            <span className="font-mono text-slate-600">{liq.numero_cheque || '-'}</span>
                          )}
                        </td>
                        
                        {/* Fecha Pago - Editable inline */}
                        <td className="px-3 py-1">
                          {isMaster ? (
                            <Input
                              type="date"
                              defaultValue={liq.pago_fecha || ''}
                              onBlur={(e) => saveField(liq, 'pago_fecha', e.target.value)}
                              className="h-7 text-xs font-mono w-32 border-transparent hover:border-slate-300 bg-transparent px-1"
                            />
                          ) : (
                            <span className="font-mono text-slate-600">{formatDate(liq.pago_fecha)}</span>
                          )}
                        </td>
                        
                        {/* Factura Transporte - Editable inline */}
                        <td className="px-3 py-1">
                          {isMaster ? (
                            <Input
                              defaultValue={liq.factura_transporte || ''}
                              onBlur={(e) => saveField(liq, 'factura_transporte', e.target.value.toUpperCase())}
                              placeholder="-"
                              className="h-7 text-xs font-mono w-24 border-transparent hover:border-slate-300 bg-transparent px-1"
                            />
                          ) : (
                            <span className="font-mono text-slate-600">{liq.factura_transporte || '-'}</span>
                          )}
                        </td>
                        
                        {/* Monto - Solo lectura */}
                        <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-600">
                          {formatCurrency(liq.a_pagar || liq.total_usd)}
                        </td>

                        {/* Recibo PDF */}
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => {
                              try {
                                ReciboPagoPDF.generate(liq);
                              } catch (err) {
                                toast.error('Error al generar el recibo');
                                console.error(err);
                              }
                            }}
                            title="Descargar recibo de pago"
                            data-testid={`recibo-btn-${liq.id}`}
                            className="inline-flex items-center gap-1 px-2 py-1.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600 hover:bg-blue-50 hover:text-blue-700 border border-slate-200 hover:border-blue-300 transition-all"
                          >
                            <FileDown size={12} />
                            PDF
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
          </>
        ) : (
          /* ═══════════════════════════════════════════════════════════════════
             SECCIÓN DE PAGOS DE GRANOS
             ═══════════════════════════════════════════════════════════════════ */
          <>
            {/* Resumen de estados GRANOS */}
            {resumenGranos && (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-amber-600" />
                      <span className="font-semibold text-amber-800">Pendientes</span>
                    </div>
                    <span className="text-2xl font-bold text-amber-700">{resumenGranos.Pendiente?.count || 0}</span>
                  </div>
                  <p className="text-sm text-amber-600 mt-2">
                    Total: {formatCurrency(resumenGranos.Pendiente?.total_usd || 0)}
                  </p>
                </div>
                
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-emerald-600" />
                      <span className="font-semibold text-emerald-800">Pagados</span>
                    </div>
                    <span className="text-2xl font-bold text-emerald-700">{resumenGranos.Pagado?.count || 0}</span>
                  </div>
                  <p className="text-sm text-emerald-600 mt-2">
                    Total: {formatCurrency(resumenGranos.Pagado?.total_usd || 0)}
                  </p>
                </div>
              </div>
            )}

            {/* Pagos por registrar (pendientes de crear) */}
            {pagosGranosPendientes.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-amber-800 flex items-center gap-2">
                    <Plus size={16} />
                    Pagos por Registrar ({pagosGranosPendientes.length})
                  </h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-amber-100 border-b border-amber-200">
                        <th className="px-3 py-2 text-left font-semibold text-amber-700">PROVEEDOR</th>
                        <th className="px-3 py-2 text-left font-semibold text-amber-700">GRANO</th>
                        <th className="px-3 py-2 text-center font-semibold text-amber-700">F. CARGA</th>
                        <th className="px-3 py-2 text-center font-semibold text-amber-700">F. VCTO</th>
                        <th className="px-3 py-2 text-right font-semibold text-amber-700">TONS</th>
                        <th className="px-3 py-2 text-right font-semibold text-amber-700">USD/TON</th>
                        <th className="px-3 py-2 text-right font-semibold text-amber-700">TOTAL USD</th>
                        <th className="px-3 py-2 text-center font-semibold text-amber-700">CAMIONES</th>
                        <th className="px-3 py-2 text-center font-semibold text-amber-700">ACCIÓN</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pagosGranosPendientes.map((pago) => (
                        <tr key={pago.key} className="border-b border-amber-100 hover:bg-amber-50">
                          <td className="px-3 py-2 font-medium text-slate-700">{pago.proveedor_nombre}</td>
                          <td className="px-3 py-2"><span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-semibold">{pago.tipo_grano}</span></td>
                          <td className="px-3 py-2 text-center font-mono">{formatDate(pago.fecha_carga)}</td>
                          <td className="px-3 py-2 text-center font-mono">{formatDate(pago.fecha_vencimiento)}</td>
                          <td className="px-3 py-2 text-right font-mono">{pago.total_ton.toLocaleString('es-CL', { minimumFractionDigits: 1 })}</td>
                          <td className="px-3 py-2 text-right font-mono">${pago.precio_usd_ton}</td>
                          <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700">${pago.total_usd.toLocaleString('es-CL', { minimumFractionDigits: 2 })}</td>
                          <td className="px-3 py-2 text-center">{pago.cantidad_camiones}</td>
                          <td className="px-3 py-2 text-center">
                            {isMaster && (
                              <Button 
                                size="sm" 
                                onClick={() => registrarPagoGranos(pago)}
                                disabled={savingId === pago.key}
                                className="h-7 text-[10px] bg-amber-600 hover:bg-amber-700"
                              >
                                {savingId === pago.key ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Registrar'}
                              </Button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Filtros para granos */}
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por proveedor, grano..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 h-9"
                  />
                </div>
                <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                  <SelectTrigger className="w-[140px] h-9">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="Pendiente">Pendiente</SelectItem>
                    <SelectItem value="Pagado">Pagado</SelectItem>
                  </SelectContent>
                </Select>
                {(searchTerm || filtroEstado !== 'all') && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => { setSearchTerm(''); setFiltroEstado('all'); }}
                    className="h-9"
                  >
                    <X className="h-4 w-4 mr-1" /> Limpiar
                  </Button>
                )}
              </div>
            </div>

            {/* Tabla de pagos registrados */}
            <div className="bg-white rounded-lg border border-slate-200 shadow-sm">
              <div className="p-3 border-b border-slate-200 bg-slate-50">
                <h3 className="text-sm font-semibold text-slate-700">Pagos Registrados ({filteredPagosGranos.length})</h3>
              </div>
              
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : filteredPagosGranos.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <Wheat className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                  <p className="text-sm">No hay pagos de granos registrados</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="px-3 py-2 text-left font-semibold text-slate-600">PROVEEDOR</th>
                        <th className="px-3 py-2 text-left font-semibold text-slate-600">GRANO</th>
                        <th className="px-3 py-2 text-center font-semibold text-slate-600">F. CARGA</th>
                        <th className="px-3 py-2 text-center font-semibold text-slate-600">F. VCTO</th>
                        <th className="px-3 py-2 text-right font-semibold text-slate-600">TONS</th>
                        <th className="px-3 py-2 text-right font-semibold text-slate-600">TOTAL USD</th>
                        <th className="px-3 py-2 text-center font-semibold text-slate-600">ESTADO</th>
                        <th className="px-3 py-2 text-center font-semibold text-slate-600">F. PAGO</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPagosGranos.map((pago) => {
                        const isPagado = pago.pago_estado === 'Pagado';
                        return (
                          <tr key={pago.id} className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="px-3 py-2 font-medium text-slate-700">{pago.proveedor_nombre}</td>
                            <td className="px-3 py-2">
                              <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 text-[10px] font-semibold">
                                {pago.tipo_grano}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-center font-mono">{formatDate(pago.fecha_carga)}</td>
                            <td className="px-3 py-2 text-center font-mono">{formatDate(pago.fecha_pago)}</td>
                            <td className="px-3 py-2 text-right font-mono">{(pago.total_kg / 1000).toLocaleString('es-CL', { minimumFractionDigits: 1 })}</td>
                            <td className="px-3 py-2 text-right font-mono font-semibold text-emerald-700">
                              ${pago.total_usd.toLocaleString('es-CL', { minimumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <button
                                onClick={() => cycleEstadoGranos(pago)}
                                disabled={!isMaster}
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-semibold border transition-all ${
                                  isPagado 
                                    ? 'bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200' 
                                    : 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-200'
                                } ${isMaster ? 'cursor-pointer' : 'cursor-default'}`}
                              >
                                {isPagado ? <CheckCircle size={12} /> : <Clock size={12} />}
                                {pago.pago_estado}
                              </button>
                            </td>
                            <td className="px-3 py-2 text-center">
                              {isMaster ? (
                                <Input
                                  type="date"
                                  defaultValue={pago.fecha_transferencia || ''}
                                  onBlur={(e) => updatePagoGranosField(pago, 'fecha_transferencia', e.target.value)}
                                  className="h-7 text-xs font-mono w-28 border-transparent hover:border-slate-300 bg-transparent px-1"
                                />
                              ) : (
                                <span className="font-mono">{formatDate(pago.fecha_transferencia)}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
};

export default Pagos;
